import { Injectable } from '@angular/core';
import { Order, QueryEntity } from '@datorama/akita';
import { compareEntities, compareEntity } from '@lycoris-nubila/ngx-lycoris';
import { isEmpty, isEqual, isNil, reject, uniqBy } from 'lodash-es';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

import { Contact } from 'contact/contact';
import {
	ContactDocument,
	ContactState,
	ContactStore,
} from 'contact/contact.store';
import { ContactSort } from 'contact/model/contact.sort';
import { ContactRepositoryPort } from 'contact/port/contact-repository.port';

import { CacheObservable } from '../../app-decorators';

@Injectable()
export class ContactRepositoryAdapter
	extends QueryEntity<ContactState>
	implements ContactRepositoryPort
{
	constructor(protected store: ContactStore) {
		super(store);
	}

	public async deleteContact(id: string): Promise<void> {
		return this.store.remove(id);
	}

	@CacheObservable()
	public observeKanbanContacts(
		ownerId: string,
		kanbanOrgModuleId: string
	): Observable<Contact[]> {
		return this.selectAll({
			filterBy: [
				(entity) =>
					entity.modulesColumn.some(
						(moduleColumn) => moduleColumn.moduleId === kanbanOrgModuleId
					),
				(entity) => entity.ownerId === ownerId,
			],
		}).pipe(
			map((documents) =>
				documents.map((document) => this.store.asEntity(document))
			),
			distinctUntilChanged(compareEntities),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public observeContacts(contactsId: string[]): Observable<Contact[]> {
		return this.selectMany(contactsId).pipe(
			distinctUntilChanged(isEqual),
			map((documents) =>
				documents.map((document) => this.store.asEntity(document))
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	@CacheObservable()
	public observeUserContacts(userId: string): Observable<Contact[]> {
		return this.selectAll({
			filterBy: [(doc) => doc.ownerId === userId],
			sortByOrder: Order.ASC,
			sortBy: 'name',
		}).pipe(
			distinctUntilChanged(isEqual),
			map((documents) =>
				documents.map((document) => this.store.asEntity(document))
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	@CacheObservable()
	public observeContact(id: string): Observable<Contact> {
		return this.selectEntity(id).pipe(
			map((document) => this.store.asEntity(document)),
			distinctUntilChanged(compareEntity),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public async saveAll(entities: Contact[]): Promise<void> {
		this.store.upsertMany(
			uniqBy(
				reject(entities, isNil).map((entity) => this.store.asDocument(entity)),
				'id'
			)
		);
	}
}
